import React, { useState, useEffect, useCallback } from "react";
import { Button, Table, Modal, Form, Spinner, Pagination } from "react-bootstrap";
import Select from "react-select";
import {
  deleteProcedimiento,
  getAllProcedimiento,
  InsertProcedimiento,
  updateProcedimiento,
  getAllTipo,
} from "../../../api/AdminApi";

const Procedimientos = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [procedimientos, setProcedimientos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [currentProcedimiento, setCurrentProcedimiento] = useState({
    idprocedimiento: null,
    procedimiento: "",
    tipo: "",
    tarifa: "",
    honorario: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // === Paginación ===
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredProcedimientos = procedimientos.filter((proc) =>
    proc.procedimiento.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchProcedimientos = useCallback(async () => {
    try {
      const response = await getAllProcedimiento();
      setProcedimientos(response);
    } catch (error) {
      console.error("Error fetching procedimientos:", error);
    }
  }, []);

  const fetchTipos = useCallback(async () => {
    try {
      const response = await getAllTipo();
      setTipos(response);
    } catch (error) {
      console.error("Error fetching tipos:", error);
    }
  }, []);

  const handleShowModal = () => {
    setCurrentProcedimiento({
      idprocedimiento: null,
      procedimiento: "",
      tipo: tipos.length > 0 ? tipos[0].tipo : "",
      tarifa: "",
      honorario: "",
    });
    setIsEditing(false);
    setShowModal(true);
    fetchTipos();
  };

  const handleCloseModal = () => setShowModal(false);

  const formatNumber = (value) => {
    if (!value) return "";
    return parseFloat(value).toLocaleString("es", {
      minimumFractionDigits: 0,
    });
  };

  const handleTarifaChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value) {
      value = parseFloat(value).toLocaleString("es", {
        minimumFractionDigits: 0,
      });
    }
    setCurrentProcedimiento({ ...currentProcedimiento, tarifa: value });
  };

  const handleHonorarioChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value) {
      value = parseFloat(value).toLocaleString("es", {
        minimumFractionDigits: 0,
      });
    }
    setCurrentProcedimiento({ ...currentProcedimiento, honorario: value });
  };

  const handleSave = async () => {
    if (
      currentProcedimiento.procedimiento.trim() === "" ||
      currentProcedimiento.tipo.trim() === "" ||
      currentProcedimiento.tarifa.trim() === "" ||
      currentProcedimiento.honorario.trim() === ""
    )
      return;

    setIsLoading(true);
    const procedimientoData = {
      idprocedimiento: currentProcedimiento.idprocedimiento,
      procedimiento: currentProcedimiento.procedimiento,
      tipo: currentProcedimiento.tipo,
      tarifa: parseFloat(currentProcedimiento.tarifa.replace(/\./g, "")),
      honorario: parseFloat(currentProcedimiento.honorario.replace(/\./g, "")),
    };

    try {
      if (isEditing) {
        await updateProcedimiento(procedimientoData);
      } else {
        await InsertProcedimiento(procedimientoData);
      }
      await fetchProcedimientos();
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar procedimiento:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (procedimiento) => {
    setCurrentProcedimiento({
      idprocedimiento: procedimiento.idprocedimiento,
      procedimiento: procedimiento.procedimiento,
      tipo: procedimiento.tipo,
      tarifa: procedimiento.tarifa.toString(),
      honorario: procedimiento.honorario ? procedimiento.honorario.toString() : "",
    });
    setIsEditing(true);
    setShowModal(true);
    fetchTipos();
  };

  const handleDelete = async (idprocedimiento) => {
    try {
      await deleteProcedimiento(idprocedimiento);
      setProcedimientos(
        procedimientos.filter((proc) => proc.idprocedimiento !== idprocedimiento)
      );
    } catch (error) {
      console.error("Error al eliminar el procedimiento:", error);
    }
  };

  const tipoOptions = tipos.map((tipo) => ({
    value: tipo.tipo,
    label: tipo.tipo,
  }));

  const selectedTipo =
    tipoOptions.find(
      (option) => option.value === currentProcedimiento.tipo
    ) || null;

  useEffect(() => {
    fetchProcedimientos();
  }, [fetchProcedimientos]);

  // === Paginación lógica ===
  const paginate = (data, currentPage) =>
    data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const pageData = paginate(filteredProcedimientos, currentPage);

  const renderPagination = (total, current, setPage) => {
    const totalPages = Math.ceil(total / itemsPerPage);
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Pagination.Item key={i} active={i === current} onClick={() => setPage(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return <Pagination className="justify-content-center mt-2">{pages}</Pagination>;
  };

  const btnIconStyle = {
    width: 30,
    height: 30,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
  };

  return (
    <div className="card">
      <div className="card-header border-0 pb-0 d-flex justify-content-between align-items-center">
        <h4 className="card-title mb-0">🧾 Procedimientos</h4>
        <Button variant="primary" size="sm" onClick={handleShowModal}>
          + Agregar
        </Button>
      </div>

      <div className="card-body">
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Buscar procedimiento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Form.Group>

        {/* Tabla con paginación */}
        <div className="table-responsive">
          <Table bordered responsive className="text-center align-middle table-sm mb-0">
            <thead>
              <tr>
                <th style={{ width: "35%" }}>Procedimiento</th>
                <th style={{ width: "20%" }}>Tipo</th>
                <th style={{ width: "15%" }}>Tarifa</th>
                <th style={{ width: "15%" }}>Honorario</th>
                <th style={{ width: "15%" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length > 0 ? (
                pageData.map((proc, index) => (
                  <tr key={index}>
                    <td style={{ whiteSpace: "pre-wrap" }}>{proc.procedimiento}</td>
                    <td>{proc.tipo}</td>
                    <td>{formatNumber(proc.tarifa)}</td>
                    <td>{formatNumber(proc.honorario)}</td>
                    <td>
                      <div className="d-flex justify-content-center gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          style={btnIconStyle}
                          onClick={() => handleEdit(proc)}
                          title="Editar"
                        >
                          <i className="fas fa-pen fa-sm"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          style={btnIconStyle}
                          onClick={() => handleDelete(proc.idprocedimiento)}
                          title="Eliminar"
                        >
                          <i className="fas fa-trash fa-sm"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-3">
                    No se encontraron resultados
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {renderPagination(filteredProcedimientos.length, currentPage, setCurrentPage)}
        </div>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Editar Procedimiento" : "Agregar Procedimiento"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del Procedimiento</Form.Label>
              <Form.Control
                type="text"
                value={currentProcedimiento.procedimiento}
                onChange={(e) =>
                  setCurrentProcedimiento({
                    ...currentProcedimiento,
                    procedimiento: e.target.value,
                  })
                }
                placeholder="Ingrese el nombre"
                disabled={isLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tipo</Form.Label>
              <Select
                options={tipoOptions}
                value={selectedTipo}
                onChange={(selectedOption) =>
                  setCurrentProcedimiento({
                    ...currentProcedimiento,
                    tipo: selectedOption ? selectedOption.value : "",
                  })
                }
                isDisabled={isLoading}
                placeholder="Seleccione un tipo"
                noOptionsMessage={() => "Sin datos"}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tarifa</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={currentProcedimiento.tarifa}
                onChange={handleTarifaChange}
                placeholder="Ingrese la tarifa"
                disabled={isLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Honorario</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={currentProcedimiento.honorario}
                onChange={handleHonorarioChange}
                placeholder="Ingrese el honorario"
                disabled={isLoading}
              />
            </Form.Group>

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleCloseModal} disabled={isLoading}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                Guardando...
              </>
            ) : isEditing ? (
              "Actualizar"
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Procedimientos;
